import { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";
import { FoodsTooltip } from "@foodpilot/foods";

interface SecretKeyFieldProps {
    secretKey: string;
    label?: string;
    onCopy?: () => void;
}

export const SecretKeyField = ({ secretKey, label, onCopy }: SecretKeyFieldProps) => {
    const [showSecret, setShowSecret] = useState(false);
    const { setSnackbarProps } = useSnackbar();
    const { t } = useTranslation();

    const toggleVisibility = () => {
        setShowSecret(!showSecret);
    };

    const copyToClipboard = () => {
        navigator.clipboard
            .writeText(secretKey)
            .then(() => {
                setSnackbarProps({
                    message: t("applications.secretCopied", "Secret key copied to clipboard"),
                    type: "success",
                });
                onCopy?.();
            })
            .catch(() => {
                setSnackbarProps({
                    message: t("applications.secretCopyFailed", "Failed to copy secret key"),
                    type: "error",
                });
            });
    };

    const maskedValue = secretKey ? "•".repeat(Math.min(secretKey.length, 16)) : "N/A";

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {label && (
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    {label}:
                </Typography>
            )}
            <Typography variant="body2" sx={{ fontFamily: "monospace" }} className="sentry-mask">
                {showSecret ? secretKey : maskedValue}
            </Typography>
            <FoodsTooltip
                title={
                    showSecret ?
                        t("applications.hideSecret", "Hide secret")
                    :   t("applications.showSecret", "Show secret")
                }
            >
                <IconButton
                    size="small"
                    onClick={toggleVisibility}
                    aria-label={showSecret ? "hide secret" : "show secret"}
                >
                    {showSecret ?
                        <VisibilityOff fontSize="small" />
                    :   <Visibility fontSize="small" />}
                </IconButton>
            </FoodsTooltip>
            <FoodsTooltip title={t("applications.copySecret", "Copy to clipboard")}>
                <IconButton size="small" onClick={copyToClipboard} aria-label="copy to clipboard">
                    <ContentCopy fontSize="small" />
                </IconButton>
            </FoodsTooltip>
        </Box>
    );
};
